<template>
  <div class="ai-visibility-detail">
    <div class="mb-10 navigate-bread-part">
      <div class="breadcrumbs-area">
        <a v-if="!embedded" class="back-to-home inline-flex align-center" @click="() => {
          $router.push(`/sites/${ site.id }/tools`)
        }">
          <span class="bread-crumb-size mainColor--text font-weight-bold">{{ site.name }}</span>
        </a>
        <span v-else class="bread-crumb-size mainColor--text font-weight-bold">{{ site.name }}</span>
        <i class="material-icons arrow-right-size">chevron_right</i>
        <detail-tracker-selector
            :site-id="siteId"
            :tracker="tracker"
            :tracker-options="trackerOptions"
            @select-tracker="handleSelectTracker"
            @create-tracker="openSetupDialog"
            @tracker-updated="handleTrackerUpdated"
            @refresh-trackers="loadTrackerOptions"
            @tracker-deleted="handleTrackerDeleted"
        />

      </div>
      <detail-top-bar
        :tracker="tracker"
        :running="runningNow"
        @run-now="handleRunNow"
    />
    </div>

    <detail-tab-nav
        :tabs="tabs"
        :site-id="siteId"
        :tracker-id="trackerId"
        :active-route-name="activeRouteName"
        :embedded="embedded"
        @select-tab="handleEmbeddedTabSelect"
    />

    <loading-state v-if="loading" label="Loading AI Visibility detail..."/>

    <error-state
        v-else-if="loadError"
        :message="loadError"
        :show-retry="false"
    />

    <template v-else>
      <detail-intro
          :tab="activeTab"
          :filters="filters"
          :range-options="rangeOptions"
          :model-options="modelOptions"
          :show-manage-questions-action="activeTab.showManageQuestionsAction"
          @update-filter="updateFilter"
          @export="handleActiveTabExport"
          @manage-questions="handleManageQuestions"
      />

      <paused-schedule-banner
          v-if="showPausedScheduleBanner"
          :tracker="tracker"
          :saving="resumingPausedTracker"
          @resume="resumePausedTracker"
      />

      <reading-legend
          v-if="activeTab.showLegend"
          :items="legend"
      />

      <component
          v-if="embedded"
          :is="activeEmbeddedComponent"
          :key="`${trackerId}-${activeEmbeddedTabKey}`"
          ref="activeTabView"
          :site-id="siteId"
          :tracker-id="trackerId"
          :detail="tracker"
          :filters="filters"
          :filter-options="filterOptions"
          :embedded="true"
          :embedded-query="embeddedTabQuery"
          @navigate-tab="handleEmbeddedTabNavigation"
          @update-query="embeddedTabQuery = $event"
          @tracker-created="handleReplacementTrackerCreated"
      />
      <router-view
          v-else
          ref="activeTabView"
          :site-id="siteId"
          :tracker-id="trackerId"
          :detail="tracker"
          :filters="filters"
          :filter-options="filterOptions"
          @tracker-created="handleReplacementTrackerCreated"
      />

      <manage-tracker-questions-dialog
          ref="manageQuestionsDialog"
          :site-id="siteId"
          :tracker-id="trackerId"
          @questions-staged="handleQuestionsStaged"
          @tracker-created="handleReplacementTrackerCreated"
      />
    </template>

    <ai-visibility-setup-dialog
        ref="setupDialogRef"
        :site-id="siteId"
        :site="site"
        :latest-tracker="latestTracker"
        @created="handleSetupTrackerCreated"
    />

  </div>
</template>

<script>
import {AI_VISIBILITY_DETAIL_TABS} from './detailTabs'
import {
  AI_VISIBILITY_DETAIL_FILTER_OPTIONS,
  AI_VISIBILITY_READING_LEGEND,
} from './detail.constants'
import DetailTopBar from './shell/DetailTopBar.vue'
import DetailTabNav from './shell/DetailTabNav.vue'
import DetailIntro from './shell/DetailIntro.vue'
import PausedScheduleBanner from './shell/PausedScheduleBanner.vue'
import ReadingLegend from './shell/ReadingLegend.vue'
import LoadingState from './ui/states/LoadingState.vue'
import ErrorState from './ui/states/ErrorState.vue'
import ManageTrackerQuestionsDialog from './questions/ManageTrackerQuestionsDialog.vue'
import AiVisibilityOverviewTab from './tabs/overview/OverviewTab.vue'
import AiVisibilityCitationsTab from './tabs/citations/CitationsTab.vue'
import AiVisibilityCompetitorsTab from './tabs/competitors/CompetitorsTab.vue'
import AiVisibilityQuestionsTab from './tabs/questions/QuestionsTab.vue'
import AiVisibilitySetupDialog from '../setup/AiVisibilitySetupDialog.vue'
import {EventBus} from '@/utils'
import {AI_VISIBILITY_RUN_PROGRESS_STARTED} from '../progress/events'
import {
  createAiVisibilityRun,
  getAiVisibilityDetail,
  getAiVisibilityProgress,
  getAiVisibilityTrackers,
  getSiteDetails,
  resumeAiVisibilityTracker
} from '../api'
import DetailTrackerSelector from "./shell/DetailTrackerSelector.vue";

const RECURRING_REFRESH_FREQUENCIES = ['weekly', 'monthly']

const PROGRESS_POLL_INTERVAL_MS = 5000
const EMBEDDED_TAB_COMPONENTS = {
  overview: 'AiVisibilityOverviewTab',
  citations: 'AiVisibilityCitationsTab',
  competitors: 'AiVisibilityCompetitorsTab',
  questions: 'AiVisibilityQuestionsTab',
}

function createEmptyTracker(siteId, trackerId) {
  return {
    id: Number(trackerId),
    siteId: Number(siteId),
    title: '',
    name: '',
    domain: '',
    status: 'inactive',
    statusTone: 'inactive',
    lastRunAt: '2026-05-21T14:53:45.259822',
    nextRunAt: '2026-05-21T14:53:45.259822',
    requiredCredits: 0,
    refreshFrequency: 'weekly',
    canRunNow: false,
    isRunning: false,
    runNowLabel: '',
    questionCount: 0,
  }
}

export default {
  name: 'AiVisibilityDetail',
  components: {
    AiVisibilitySetupDialog,
    DetailTrackerSelector,
    DetailTopBar,
    DetailTabNav,
    DetailIntro,
    PausedScheduleBanner,
    ReadingLegend,
    LoadingState,
    ErrorState,
    ManageTrackerQuestionsDialog,
    AiVisibilityOverviewTab,
    AiVisibilityCitationsTab,
    AiVisibilityCompetitorsTab,
    AiVisibilityQuestionsTab,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    embeddedSiteId: {
      type: [Number, String],
      default: null,
    },
    embeddedTrackerId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    const initialSiteId = this.embedded ? this.embeddedSiteId : this.$route.params.siteId
    const initialTrackerId = this.embedded ? this.embeddedTrackerId : this.$route.params.trackerId
    return {
      loading: false,
      loadError: '',
      runningNow: false,
      resumingPausedTracker: false,
      tracker: createEmptyTracker(initialSiteId, initialTrackerId),
      trackerOptions: [],
      selectedEmbeddedTrackerId: null,
      activeEmbeddedTabKey: 'overview',
      embeddedTabQuery: {},
      filters: {
        range: 'all',
        model: 'all',
      },
      runProgress: null,
      progressTimer: null,
      progressRequestInFlight: false,
      site: {}
    }
  },
  computed: {
    tabs() {
      return AI_VISIBILITY_DETAIL_TABS
    },
    siteId() {
      if (this.embedded) {
        return Number(this.embeddedSiteId)
      }
      return Number(this.$route.params.siteId)
    },
    trackerId() {
      if (this.embedded) {
        return Number(this.selectedEmbeddedTrackerId || this.embeddedTrackerId)
      }
      return Number(this.$route.params.trackerId)
    },
    activeTab() {
      if (this.embedded) {
        return this.tabs.find(tab => tab.key === this.activeEmbeddedTabKey) || this.tabs[0]
      }
      return this.tabs.find(tab => tab.routeName === this.$route.name) || this.tabs[0]
    },
    activeRouteName() {
      return this.activeTab.routeName
    },
    activeEmbeddedComponent() {
      return EMBEDDED_TAB_COMPONENTS[this.activeEmbeddedTabKey] || EMBEDDED_TAB_COMPONENTS.overview
    },
    filterOptions() {
      return AI_VISIBILITY_DETAIL_FILTER_OPTIONS
    },
    legend() {
      return this.activeTab.legend || AI_VISIBILITY_READING_LEGEND
    },
    rangeOptions() {
      return this.filterOptions.ranges
    },
    modelOptions() {
      return this.filterOptions.models
    },
    latestTracker() {
      if (this.tracker && this.tracker.id != null) {
        return this.tracker
      }
      if (this.trackerOptions.length > 0) {
        return this.trackerOptions[0]
      }
      return null
    },
    showPausedScheduleBanner() {
      return this.tracker &&
        this.tracker.status === 'inactive' &&
        RECURRING_REFRESH_FREQUENCIES.includes(this.tracker.refreshFrequency)
    },
  },
  watch: {
    '$route.params.siteId'() {
      if (this.embedded) {
        return
      }
      this.stopProgressPolling()
      this.loadDetail()
      this.loadTrackerOptions()
      this.loadSiteDetails()
    },
    '$route.params.trackerId'() {
      if (this.embedded) {
        return
      }
      this.stopProgressPolling()
      this.loadDetail()
    },
    embeddedSiteId() {
      if (!this.embedded) {
        return
      }
      this.stopProgressPolling()
      this.selectedEmbeddedTrackerId = null
      this.embeddedTabQuery = {}
      this.loadDetail()
      this.loadTrackerOptions()
      this.loadSiteDetails()
    },
    embeddedTrackerId() {
      if (!this.embedded) {
        return
      }
      this.stopProgressPolling()
      this.selectedEmbeddedTrackerId = null
      this.embeddedTabQuery = {}
      this.loadDetail()
    },
  },
  beforeMount() {
    this.loadDetail()
    this.loadTrackerOptions()
    this.loadSiteDetails()
  },
  beforeDestroy() {
    this.stopProgressPolling()
  },
  methods: {
    loadDetail() {
      this.loading = true
      this.loadError = ''

      return getAiVisibilityDetail(this.siteId, this.trackerId)
          .then((detail) => {
            this.tracker = detail.data
            if (!this.filters.range && this.rangeOptions.length) {
              this.filters.range = this.rangeOptions[0].value
            }
            if (!this.filters.model && this.modelOptions.length) {
              this.filters.model = this.modelOptions[0].value
            }
            this.syncProgressPolling()
          })
          .catch(() => {
            this.tracker = createEmptyTracker(this.siteId, this.trackerId)
            this.loadError = 'Could not load AI Visibility detail.'
          })
          .finally(() => {
            this.loading = false
          })
    },
    loadTrackerOptions() {
      if (!this.siteId) {
        this.trackerOptions = []
        return Promise.resolve()
      }

      return getAiVisibilityTrackers(this.siteId)
          .then((response) => {
            this.trackerOptions = response.data.trackers || []
          })
          .catch(() => {
            this.trackerOptions = []
          })
    },
    loadSiteDetails() {
      if (!this.siteId) {
        this.site = {}
        return Promise.resolve()
      }

      return getSiteDetails(this.siteId)
          .then((response) => {
            this.site = response.data.site || {}
          })
          .catch(() => {
            this.site = {}
          })
    },
    updateFilter({key, value}) {
      this.filters = {
        ...this.filters,
        [key]: value,
      }
    },
    handleRunNow() {
      if (this.runningNow || this.tracker.isRunning) {
        return Promise.resolve()
      }

      this.runningNow = true
      return createAiVisibilityRun(this.siteId, this.trackerId)
          .then((response) => {
            const run = response.data.run || {}
            const isRunning = ['queued', 'running'].includes(run.status)
            this.tracker = {
              ...this.tracker,
              isRunning,
            }
            this.$notify({
              group: 'info',
              type: isRunning ? 'success' : 'error',
              text: isRunning
                  ? 'AI Visibility run queued.'
                  : 'Could not queue AI Visibility run.',
            })
            if (isRunning) {
              EventBus.$emit(AI_VISIBILITY_RUN_PROGRESS_STARTED, {
                siteId: this.siteId,
                tracker: this.tracker,
              })
              this.startProgressPolling()
            }
            return Promise.all([
              this.loadDetail(),
              this.loadTrackerOptions(),
            ])
          })
          .catch((error) => {
            this.$notify({
              group: 'info',
              type: 'error',
              text: this.getRunNowErrorMessage(error),
            })
          })
          .finally(() => {
            this.runningNow = false
          })
    },
    getRunNowErrorMessage(error) {
      return error?.response?.data?.error ||
          error?.response?.data?.msg ||
          'Could not queue AI Visibility run.'
    },
    resumePausedTracker() {
      if (
        this.resumingPausedTracker ||
        !this.tracker ||
        this.tracker.id == null
      ) {
        return Promise.resolve()
      }

      this.resumingPausedTracker = true
      return resumeAiVisibilityTracker(this.siteId, this.tracker.id)
          .then((response) => {
            const updatedTracker = response.data.tracker || {
              ...this.tracker,
              status: 'active',
            }
            this.handleTrackerUpdated(updatedTracker)
            this.loadTrackerOptions()
            this.$notify({
              group: 'info',
              type: 'success',
              text: 'Report resumed. Your previously active report is now paused. Only one report runs at a time.',
              duration: 5000
            })
          })
          .catch(() => {
            this.$notify({
              group: 'info',
              type: 'error',
              text: 'Could not resume schedule.',
            })
          })
          .finally(() => {
            this.resumingPausedTracker = false
          })
    },
    syncProgressPolling() {
      if (this.tracker.isRunning) {
        this.startProgressPolling()
        return
      }
      this.stopProgressPolling()
    },
    startProgressPolling() {
      if (!this.siteId || !this.trackerId || this.progressTimer) {
        return
      }

      if (!this.runProgress) {
        this.runProgress = {
          percentage: 0,
          isComplete: false,
          hasReadyRun: true,
        }
      }
      this.pollProgress()
      this.progressTimer = setInterval(
          this.pollProgress,
          PROGRESS_POLL_INTERVAL_MS,
      )
    },
    pollProgress() {
      if (this.progressRequestInFlight || !this.siteId || !this.trackerId) {
        return Promise.resolve()
      }

      this.progressRequestInFlight = true
      return getAiVisibilityProgress(this.siteId, this.trackerId)
          .then((response) => {
            this.runProgress = {
              percentage: 0,
              isComplete: false,
              hasReadyRun: true,
              ...(response.data || {}),
            }
            if (this.runProgress.isComplete) {
              this.handleProgressComplete()
            }
          })
          .catch(() => {
            this.stopProgressPolling()
          })
          .finally(() => {
            this.progressRequestInFlight = false
          })
    },
    handleProgressComplete() {
      this.clearProgressTimer()
      Promise.all([
        this.loadDetail(),
        this.loadTrackerOptions(),
      ]).then(() => {
        this.refreshActiveTab()
      })
    },
    refreshActiveTab() {
      const activeTabView = this.$refs.activeTabView
      if (!activeTabView) {
        return
      }

      const loadMethodNames = [
        'loadOverview',
        'loadCitations',
        'loadCompetitors',
        'loadQuestions',
      ]
      const loadMethodName = loadMethodNames.find((methodName) => (
          typeof activeTabView[methodName] === 'function'
      ))
      if (loadMethodName) {
        activeTabView[loadMethodName]()
      }
    },
    clearProgressTimer() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    stopProgressPolling() {
      this.clearProgressTimer()
      this.runProgress = null
      this.progressRequestInFlight = false
    },
    handleActiveTabExport() {
      const activeTabView = this.$refs.activeTabView
      if (!activeTabView || typeof activeTabView.handleExport !== 'function') {
        return
      }

      activeTabView.handleExport()
    },
    handleManageQuestions() {
      const manageQuestionsDialog = this.$refs.manageQuestionsDialog
      if (!manageQuestionsDialog || typeof manageQuestionsDialog.showDialog !== 'function') {
        return
      }

      manageQuestionsDialog.showDialog()
    },
    openSetupDialog() {
      const setupDialog = this.$refs.setupDialogRef
      if (!setupDialog || typeof setupDialog.showDialog !== 'function') {
        return
      }

      setupDialog.showDialog()
    },
    handleQuestionsStaged(payload) {
      const activeTabView = this.$refs.activeTabView
      if (!activeTabView || typeof activeTabView.stageQuestionChanges !== 'function') {
        return
      }

      activeTabView.stageQuestionChanges(payload)
    },
    handleSetupTrackerCreated(tracker) {
      if (!tracker || tracker.id == null) {
        this.loadTrackerOptions()
        return
      }

      EventBus.$emit(AI_VISIBILITY_RUN_PROGRESS_STARTED, {
        siteId: this.siteId,
        tracker,
        fromCreation: true,
      })
      this.loadTrackerOptions()
      this.handleSelectTracker(tracker)
    },
    handleReplacementTrackerCreated(payload) {
      const tracker = payload && payload.tracker
      if (!tracker || tracker.id == null) {
        this.loadDetail()
        return
      }

      this.loadTrackerOptions()
      this.handleSelectTracker(tracker)
    },
    handleSelectTracker(tracker) {
      if (!tracker || tracker.id == null) {
        return
      }

      if (this.embedded) {
        this.selectedEmbeddedTrackerId = tracker.id
        this.embeddedTabQuery = {}
        this.stopProgressPolling()
        this.loadDetail()
        return
      }

      const routeName = this.$route.name === 'AiVisibilityDetail'
          ? 'AiVisibilityOverview'
          : (this.$route.name || 'AiVisibilityOverview')

      this.$router.push({
        name: routeName,
        params: {
          siteId: String(this.siteId),
          trackerId: String(tracker.id),
        },
      })
    },
    handleTrackerUpdated(updatedTracker) {
      if (!updatedTracker || updatedTracker.id == null) {
        return
      }

      this.trackerOptions = this.trackerOptions.map((tracker) => (
          String(tracker.id) === String(updatedTracker.id)
            ? { ...tracker, ...updatedTracker }
            : tracker
      ))

      if (String(this.tracker.id) === String(updatedTracker.id)) {
        this.tracker = {
          ...this.tracker,
          ...updatedTracker,
        }
      }
    },
    handleTrackerDeleted() {
      if (this.embedded) {
        this.$emit('tracker-deleted')
        return
      }

      this.$router.push(`/sites/${this.siteId}/tools`)
    },
    handleEmbeddedTabSelect(tab) {
      if (!tab || !tab.key) {
        return
      }
      this.activeEmbeddedTabKey = tab.key
      this.embeddedTabQuery = {}
    },
    handleEmbeddedTabNavigation(payload) {
      const routeName = typeof payload === 'string' ? payload : payload?.name
      const tab = this.tabs.find(item => item.routeName === routeName)
      if (!tab) {
        return
      }
      this.activeEmbeddedTabKey = tab.key
      this.embeddedTabQuery = payload?.query || {}
    },
  },
}
</script>

<style lang="scss" scoped>

.breadcrumbs-area {
  margin-top: 0;
}

.ai-visibility-detail {
  min-height: 100%;
  background: var(--v-gray7Color-base);
  color: var(--v-mainColor-base);
  padding: 0 28px 36px;
}

@media (max-width: 768px) {
  .ai-visibility-detail {
    padding: 18px 16px 28px;
  }
}
</style>
